/**
 * EditRecurrenceDialog - Dialog para escolher como editar lançamento recorrente
 * 
 * Oferece 3 opções:
 * 1. Apenas este lançamento
 * 2. Este e os próximos lançamentos
 * 3. Todos os lançamentos desta série
 */

import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Edit, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../../lib/utils/format';
import { cn } from '../../../components/ui/utils';
import type { CashFlowEntry } from '../../../types/database';

export type EditOption = 'single' | 'future' | 'all';

interface EditRecurrenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: CashFlowEntry;
  seriesCount: {
    total: number;
    future: number;
  };
  onConfirm: (option: EditOption) => void;
  isUpdating?: boolean;
}

export function EditRecurrenceDialog({
  open,
  onOpenChange,
  transaction,
  seriesCount,
  onConfirm,
  isUpdating = false,
}: EditRecurrenceDialogProps) {
  const [selectedOption, setSelectedOption] = useState<EditOption>('single');

  const handleConfirm = () => {
    onConfirm(selectedOption);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-lg">
              <Edit className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-lg">
                Editar Lançamento Recorrente
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-sm text-muted-foreground pt-2">
            Este lançamento faz parte de uma série recorrente. Escolha como deseja aplicar as alterações:
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Transaction Info */}
        <div className="p-3 bg-muted/30 rounded-lg space-y-1">
          <p className="text-sm font-medium">{transaction.description}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{transaction.frequency === 'monthly' ? 'Mensal' : 
                   transaction.frequency === 'weekly' ? 'Semanal' : 
                   transaction.frequency === 'daily' ? 'Diário' :
                   transaction.frequency === 'quarterly' ? 'Trimestral' : 'Anual'}</span>
            <span className="font-medium text-foreground">
              {formatCurrency(transaction.amount, transaction.currency)}
            </span>
          </div>
        </div>

        {/* Options */}
        <RadioGroup
          value={selectedOption}
          onValueChange={(value) => setSelectedOption(value as EditOption)}
          className="space-y-3"
        >
          {/* Option 1: Single */}
          <label
            htmlFor="edit-option-single"
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
              selectedOption === 'single'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem value="single" id="edit-option-single" className="mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Apenas este lançamento</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Os demais lançamentos da série não serão alterados
              </p>
            </div>
          </label>

          {/* Option 2: Future */}
          <label
            htmlFor="edit-option-future"
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
              selectedOption === 'future'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem value="future" id="edit-option-future" className="mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Este e os próximos</span>
                <span className="text-xs font-medium text-muted-foreground">
                  {seriesCount.future} {seriesCount.future === 1 ? 'lançamento' : 'lançamentos'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Atualiza este e todos os futuros desta série
              </p>
            </div>
          </label>

          {/* Option 3: All */}
          <label
            htmlFor="edit-option-all"
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
              selectedOption === 'all'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem value="all" id="edit-option-all" className="mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Todos os lançamentos</span>
                <span className="text-xs font-medium text-muted-foreground">
                  {seriesCount.total} {seriesCount.total === 1 ? 'lançamento' : 'lançamentos'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Atualiza toda a série recorrente
              </p>
            </div>
          </label>
        </RadioGroup>

        {/* Info */}
        <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-900 dark:text-blue-100">
            <strong>Atenção:</strong> 
            {selectedOption === 'all' && ' Todos os lançamentos da série serão atualizados com os novos dados.'}
            {selectedOption === 'future' && ' Este e todos os lançamentos futuros serão atualizados com os novos dados.'}
            {selectedOption === 'single' && ' Apenas este lançamento será atualizado. Isso o desvinculará da série recorrente.'}
          </p>
        </div>

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel disabled={isUpdating}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isUpdating}
            className={cn(
              isUpdating && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isUpdating ? 'Atualizando...' : 'Confirmar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

